import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FIELD_TYPES,
  FORM_SCHEMA_ENTITY_TYPES,
  type FormField,
  type FormFieldType,
  type FormSchemaDocument,
  type FormSchemaEntityType,
} from '@mygmao/shared';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  Plus,
  Save,
  Settings,
  Trash2,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { DynamicFormRenderer } from '../components/DynamicFormRenderer';
import { BaseFieldsPreview } from '../components/form-builder/BaseFieldsPreview';

/**
 * Page /admin/formulaires (US-ADM-08).
 *
 * Liste les schémas Form Builder + permet d'en créer/éditer un.
 * L'éditeur propose drag-drop des champs (dnd-kit), édition inline,
 * et une prévisualisation temps réel via `<DynamicFormRenderer>`.
 */

interface FormSchemaRow {
  id: string;
  entity_type: FormSchemaEntityType;
  entity_subtype: string | null;
  name: string;
  description: string | null;
  version: number;
  is_active: boolean;
  updated_at: string;
}

export function AdminFormBuilderPage() {
  const [items, setItems] = useState<FormSchemaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: FormSchemaRow[] }>('/admin/form-schemas');
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  if (editingId || creating) {
    return (
      <FormSchemaEditor
        id={editingId}
        onClose={() => {
          setEditingId(null);
          setCreating(false);
          refresh();
        }}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Settings className="w-7 h-7 mr-2 text-primary-600" />
        Formulaires dynamiques
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Configure les champs avancés affichés dans les formulaires de création/édition. Un seul
            schéma actif par entité+sous-type.
          </p>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau schéma
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Aucun schéma. Crée le premier pour ajouter des champs dynamiques.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Entité
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nom
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actif
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {s.entity_type}
                    </span>
                    {s.entity_subtype && (
                      <span className="ml-1 text-xs text-gray-500">/ {s.entity_subtype}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">v{s.version}</td>
                  <td className="px-4 py-3 text-sm">
                    {s.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      onClick={() => setEditingId(s.id)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Éditer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Éditeur de schéma
// ─────────────────────────────────────────────────────────────────────────────

interface EditorMeta {
  entity_type: FormSchemaEntityType;
  entity_subtype: string;
  name: string;
  description: string;
  is_active: boolean;
}

/**
 * Variante locale de `FormField` enrichie d'un identifiant stable côté UI
 * (`_uid`). Cet `_uid` sert d'unique key pour React et @dnd-kit, indépendant
 * de la `key` métier qui peut changer (auto-dérivation depuis le label). Sans
 * ça, modifier le label provoquerait un unmount complet du composant et la
 * perte du focus à chaque caractère. L'`_uid` est strippé avant l'envoi serveur.
 */
type EditorField = FormField & { _uid: string };

function newUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `uid_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function FormSchemaEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [meta, setMeta] = useState<EditorMeta>({
    entity_type: 'user',
    entity_subtype: '',
    name: '',
    description: '',
    is_active: false,
  });
  const [fields, setFields] = useState<EditorField[]>([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get<{
          data: EditorMeta & { schema: FormSchemaDocument };
        }>(`/admin/form-schemas/${id}`);
        const d = res.data.data;
        setMeta({
          entity_type: d.entity_type,
          entity_subtype: d.entity_subtype ?? '',
          name: d.name,
          description: d.description ?? '',
          is_active: d.is_active,
        });
        setFields(d.schema.fields.map((f) => ({ ...f, _uid: newUid() })));
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function save() {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...meta,
        entity_subtype: meta.entity_subtype || null,
        description: meta.description || null,
        // Strip l'`_uid` interne avant l'envoi serveur (cf. type EditorField).
        schema: { fields: fields.map(({ _uid: _ignored, ...f }) => f) },
      };
      if (id) {
        await api.patch(`/admin/form-schemas/${id}`, {
          name: payload.name,
          description: payload.description,
          schema: payload.schema,
        });
      } else {
        await api.post('/admin/form-schemas', payload);
      }
      onClose();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    if (!id) return;
    try {
      await api.post(`/admin/form-schemas/${id}/${meta.is_active ? 'deactivate' : 'activate'}`);
      setMeta((m) => ({ ...m, is_active: !m.is_active }));
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  function addField() {
    const newField: EditorField = {
      _uid: newUid(),
      key: `field_${fields.length + 1}`,
      label: `Nouveau champ ${fields.length + 1}`,
      type: 'text',
      required: false,
    };
    setFields((f) => [...f, newField]);
  }

  function removeField(idx: number) {
    setFields((f) => f.filter((_, i) => i !== idx));
  }

  function updateField(idx: number, patch: Partial<FormField>) {
    setFields((f) =>
      f.map((field, i) => {
        if (i !== idx) return field;
        const next = { ...field, ...patch };
        // Auto-dérive la `key` à partir du `label` tant que l'utilisateur n'a
        // pas explicitement édité la clé. Heuristique : si la clé actuelle
        // correspond au slug du label précédent (ou au défaut `field_<n>`
        // posé par addField), on la régénère depuis le nouveau label.
        if (
          patch.label !== undefined &&
          patch.key === undefined &&
          isAutoDerivedKey(field.key, field.label, idx)
        ) {
          const derived = slugifyFieldKey(next.label);
          if (derived) next.key = derived;
        }
        return next;
      }),
    );
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f._uid === active.id);
    const newIndex = fields.findIndex((f) => f._uid === over.id);
    if (oldIndex >= 0 && newIndex >= 0) {
      setFields((f) => arrayMove(f, oldIndex, newIndex));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          {id ? 'Éditer le schéma' : 'Nouveau schéma'}
        </h1>
        <div className="space-x-2">
          <button
            onClick={() => setShowPreview((s) => !s)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showPreview ? 'Masquer prévisualisation' : 'Prévisualisation'}
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={save}
            disabled={saving || fields.length === 0 || !meta.name}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Métadonnées + liste champs */}
        <div className={showPreview ? 'lg:col-span-2 space-y-4' : 'lg:col-span-3 space-y-4'}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Métadonnées
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Entité cible">
                <select
                  disabled={Boolean(id)}
                  value={meta.entity_type}
                  onChange={(e) =>
                    setMeta((m) => ({ ...m, entity_type: e.target.value as FormSchemaEntityType }))
                  }
                  className={inputClass(Boolean(id))}
                >
                  {FORM_SCHEMA_ENTITY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Sous-type (optionnel)">
                <input
                  type="text"
                  disabled={Boolean(id)}
                  value={meta.entity_subtype}
                  onChange={(e) => setMeta((m) => ({ ...m, entity_subtype: e.target.value }))}
                  className={inputClass(Boolean(id))}
                  placeholder="ex. production"
                />
              </Field>
              <Field label="Nom du schéma" className="col-span-2">
                <input
                  type="text"
                  value={meta.name}
                  onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
                  className={inputClass()}
                />
              </Field>
              <Field label="Description (optionnel)" className="col-span-2">
                <textarea
                  rows={2}
                  value={meta.description}
                  onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
                  className={inputClass()}
                />
              </Field>
              {id && (
                <div className="col-span-2 flex items-center justify-between border-t border-gray-200 pt-3">
                  <span className="text-sm text-gray-700">
                    Statut :{' '}
                    {meta.is_active ? (
                      <span className="inline-flex items-center text-green-600 font-medium">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Actif
                      </span>
                    ) : (
                      <span className="text-gray-500">Inactif</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={toggleActive}
                    className="px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {meta.is_active ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Champs ({fields.length})
              </h2>
              <button
                onClick={addField}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un champ
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f._uid)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {fields.map((field, idx) => (
                    <SortableFieldRow
                      key={field._uid}
                      field={field}
                      onChange={(patch) => updateField(idx, patch)}
                      onRemove={() => removeField(idx)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {fields.length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-6">
                Aucun champ. Clique "Ajouter un champ" pour commencer.
              </p>
            )}
          </div>
        </div>

        {/* Prévisualisation */}
        {showPreview && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-6 h-fit max-h-[calc(100vh-3rem)] overflow-y-auto">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Prévisualisation
            </h2>
            <BaseFieldsPreview entity={meta.entity_type} />
            {fields.length > 0 && (
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-2">
                  Champs personnalisés
                </h3>
              </div>
            )}
            <DynamicFormRenderer
              schema={{ fields }}
              values={previewValues}
              onChange={setPreviewValues}
            />
            {fields.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                Ajoute des champs pour visualiser comment ils apparaîtront en complément des champs
                de base ci-dessus.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SortableFieldRow({
  field,
  onChange,
  onRemove,
}: {
  field: EditorField;
  onChange(patch: Partial<FormField>): void;
  onRemove(): void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field._uid,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border border-gray-200 bg-white p-3 space-y-2"
    >
      <div className="flex items-start space-x-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          aria-label="Déplacer"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <input
            type="text"
            value={field.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Libellé"
            className={inputClass()}
          />
          <input
            type="text"
            value={field.key}
            onChange={(e) => onChange({ key: e.target.value })}
            placeholder="clé_technique"
            className={`${inputClass()} font-mono text-xs`}
          />
          <select
            value={field.type}
            onChange={(e) => onChange({ type: e.target.value as FormFieldType })}
            className={inputClass()}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onChange({ required: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Obligatoire</span>
          </label>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:bg-red-50 p-1.5 rounded"
          aria-label="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {(field.type === 'select' || field.type === 'multiselect' || field.type === 'radio') && (
        <div className="ml-7">
          <p className="text-xs text-gray-500 mb-1">Options (key:label, une par ligne)</p>
          <textarea
            rows={3}
            value={(field.options ?? []).map((o) => `${o.value}:${o.label}`).join('\n')}
            onChange={(e) => {
              const options = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .map((l) => {
                  const [value, label] = l.split(':');
                  return { value: value?.trim() ?? '', label: (label ?? value ?? '').trim() };
                })
                .filter((o) => o.value);
              onChange({ options });
            }}
            className={`${inputClass()} font-mono text-xs`}
            placeholder="active:Actif&#10;inactive:Inactif"
          />
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function inputClass(disabled?: boolean): string {
  const base =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';
  return disabled ? `${base} bg-gray-50 cursor-not-allowed` : base;
}

/**
 * Convertit un libellé en `snake_case` ASCII safe pour servir d'identifiant
 * technique (clé `custom_fields`).
 *
 *   "Surface totale (m²)" → "surface_totale_m"
 *   "Date d'inspection"   → "date_d_inspection"
 *   "Présent ?"           → "present"
 *
 * Garde uniquement [a-z0-9_], collapse les séparateurs successifs en un seul
 * underscore et trim les bords. Si le résultat commence par un chiffre, on
 * le préfixe par `f_` (les clés JS doivent commencer par une lettre ou `_`).
 */
function slugifyFieldKey(label: string): string {
  const ascii = label.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const slug = ascii
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  if (!slug) return '';
  return /^[0-9]/.test(slug) ? `f_${slug}` : slug;
}

/**
 * Détermine si la clé d'un champ peut être considérée comme « auto-générée »
 * (donc remplaçable au prochain update du label) :
 *  - clé par défaut `field_<n>` produite par `addField`
 *  - clé qui correspond exactement au slug du label actuel
 * Dans les autres cas (clé personnalisée par l'utilisateur), on ne touche pas.
 */
function isAutoDerivedKey(currentKey: string, currentLabel: string, index: number): boolean {
  if (currentKey === `field_${index + 1}`) return true;
  return currentKey === slugifyFieldKey(currentLabel);
}
